import React from 'react';
import styled, { css, keyframes } from 'styled-components';
import { C, DrawnFooter, DrawnNav, EASE_OUT, hover, Lettering, NAV, NavItem, Socials, Buy } from '../kit';

/**
 * The drawn pieces /ens and /governance share with the home page: the scene header with the
 * nav over it, the sky with its print dots, ink panels with a hand drawn edge, chunky buttons
 * and fields, status notes, and the foot with the gang.
 */

/** The home menu, with every in-page link sent back to the home page. */
export const SUB_NAV: NavItem[] = NAV.map((item) =>
  item.label === 'REFERRAL'
    ? { label: 'DOLLARIZE', href: '/#dollarize' }
    : item.href === '#top'
      ? { ...item, href: '/' }
      : item.href.startsWith('#')
        ? { ...item, href: `/${item.href}` }
        : item
);

const PRINT = 'radial-gradient(rgba(255, 255, 255, 0.35) 1.5px, transparent 1.7px)';

export const Page = styled.div`
  font-family: 'Ubuntu', sans-serif;
  color: ${C.ink};
  background-color: ${C.light};
  background-image: ${PRINT};
  background-size: 18px 18px;
  min-height: 100vh;
  overflow-x: hidden;
`;

/* ---------------------------------------------------------------- header */

export const SceneHeader: React.FC<{
  scene: string;
  sky: string;
  position?: string;
  title: React.ReactNode;
  lead: React.ReactNode;
  children?: React.ReactNode;
}> = ({ scene, sky, position = '70% 50%', title, lead, children }) => (
  <Header style={{ background: sky }}>
    <img className="scene" src={scene} alt="" aria-hidden="true" style={{ objectPosition: position }} />
    <div className="over">
      <DrawnNav color={C.white} items={SUB_NAV} home="/" />
      <div className="copy">
        <h1>{title}</h1>
        <p className="lead">{lead}</p>
        {children && <div className="actions">{children}</div>}
      </div>
    </div>
    <Edge aria-hidden="true">
      <svg viewBox="0 0 1440 90" preserveAspectRatio="none">
        <path d="M0 40 C 220 10, 420 70, 720 38 S 1200 12, 1440 44 L1440 90 L0 90 Z" fill={C.light} />
        <path d="M0 40 C 220 10, 420 70, 720 38 S 1200 12, 1440 44" fill="none" stroke={C.ink} strokeWidth="4" strokeLinecap="round" />
      </svg>
    </Edge>
  </Header>
);

const Header = styled.header`
  position: relative;
  .scene {
    display: block;
    width: 100%;
    /* the whole drawing when it fits, like the home header; cropped only on short screens */
    height: max(480px, min(calc(100vw * 941 / 1672), calc(100vh - 90px)));
    object-fit: cover;
  }
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 1;
    background: linear-gradient(90deg, rgba(11, 19, 43, 0.62) 0%, rgba(11, 19, 43, 0.3) 38%, rgba(11, 19, 43, 0) 62%),
      linear-gradient(180deg, rgba(0, 0, 0, 0.45) 0%, rgba(0, 0, 0, 0) 22%);
    pointer-events: none;
  }
  .over {
    position: absolute;
    inset: 0 0 auto 0;
    z-index: 2;
  }
  .copy {
    max-width: 1320px;
    margin: 0 auto;
    padding: clamp(10px, 4vh, 48px) clamp(16px, 4vw, 40px) 0;
    color: ${C.white};
  }
  h1 {
    margin: 0 0 22px -6px;
    line-height: 1;
  }
  .lead {
    max-width: 30ch;
    text-wrap: balance;
    margin: 0;
    font-size: clamp(19px, 1.6vw, 23px);
    font-weight: 500;
    line-height: 1.45;
    text-shadow: 0 1px 10px rgba(0, 0, 0, 0.45);
  }
  .actions {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    margin-top: 30px;
  }
`;

/** The connected wallet as a small sticker, with its disconnect link. */
export const WalletChip: React.FC<{ address: string; onDisconnect: () => void; disabled?: boolean }> = ({ address, onDisconnect, disabled }) => (
  <Chip>
    <i aria-hidden="true" />
    <code>{`${address.slice(0, 6)}...${address.slice(-4)}`}</code>
    <button type="button" onClick={onDisconnect} disabled={disabled}>
      Disconnect
    </button>
  </Chip>
);

const Chip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 18px;
  padding: 4px 6px 4px 12px;
  background: ${C.white};
  border: 3px solid ${C.ink};
  border-radius: 999px;
  box-shadow: 2px 3px 0 ${C.ink};
  font-size: 14px;
  i {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${C.green};
    border: 2px solid ${C.ink};
  }
  code {
    font: 700 14px 'Ubuntu Mono', monospace;
  }
  button {
    min-height: 36px;
    padding: 4px 10px;
    border: 0;
    background: none;
    font: 700 13px 'Ubuntu', sans-serif;
    text-decoration: underline;
    text-underline-offset: 3px;
    cursor: pointer;
    color: ${C.ink};
  }
`;

const Edge = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: -2px;
  z-index: 3;
  line-height: 0;
  svg {
    display: block;
    width: 100%;
    height: clamp(36px, 5vw, 72px);
  }
`;

/** The page title in the lettering of the home titles. */
export const PageTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Lettering $size="clamp(52px, 6.4vw, 96px)" $tilt={-3}>
    {children}
  </Lettering>
);

export const SectionTitle: React.FC<{ children: React.ReactNode; id?: string; tilt?: number; tone?: 'yellow' | 'pink' }> = ({ children, id, tilt = -2, tone = 'yellow' }) => (
  <h2 id={id} style={{ margin: '0 0 24px' }}>
    <Lettering $size="clamp(32px, 3.4vw, 50px)" $tilt={tilt} style={tone === 'pink' ? PINK : undefined}>
      {children}
    </Lettering>
  </h2>
);

/** The pink sticker variant of the lettering, as in the home ZEUS title. */
export const PINK: React.CSSProperties = {
  backgroundImage: `radial-gradient(circle at 50% 50%, ${C.white} 26%, transparent 29%), linear-gradient(180deg, #ff8fcf 20%, ${C.pink} 100%)`,
  backgroundSize: '8px 8px, 100% 100%',
};

/* ---------------------------------------------------------------- layout */

export const Wrap = styled.div`
  max-width: 1240px;
  margin: 0 auto;
  padding: clamp(36px, 7vh, 80px) clamp(16px, 4vw, 40px);
`;

export const Grid = styled.div<{ $cols?: string }>`
  display: grid;
  grid-template-columns: ${(p) => p.$cols || 'minmax(0, 1fr) minmax(0, 1fr)'};
  gap: clamp(24px, 4vw, 56px);
  align-items: start;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const Prose = styled.div`
  max-width: 60ch;
  font-size: clamp(17px, 1.3vw, 19px);
  line-height: 1.62;
  p {
    margin: 0 0 1em;
  }
  a {
    color: inherit;
    font-weight: 700;
    text-decoration-thickness: 2px;
    text-underline-offset: 3px;
  }
  ul {
    margin: 0 0 1em;
    padding: 0;
    list-style: none;
  }
  li {
    position: relative;
    padding-left: 30px;
    margin-bottom: 10px;
  }
  li::before {
    content: '';
    position: absolute;
    left: 2px;
    top: 0.5em;
    width: 14px;
    height: 14px;
    border: 3px solid ${C.ink};
    border-radius: 50%;
    background: ${C.yellow};
  }
`;

/** A panel drawn in ink: uneven corners, a heavy shadow, a slight tilt that settles on hover. */
export const Panel = styled.div<{ $tone?: 'paper' | 'yellow' | 'ink'; $tilt?: number }>`
  position: relative;
  padding: clamp(22px, 2.6vw, 36px);
  background: ${(p) => (p.$tone === 'yellow' ? C.yellow : p.$tone === 'ink' ? C.ink : C.paper)};
  color: ${(p) => (p.$tone === 'ink' ? C.white : C.ink)};
  border: 4px solid ${C.ink};
  border-radius: 28px 36px 24px 40px / 34px 26px 38px 28px;
  box-shadow: 6px 8px 0 ${C.ink};
  transform: rotate(${(p) => p.$tilt ?? 0}deg);
  transition: transform 380ms ${EASE_OUT};
`;

/* ---------------------------------------------------------------- controls */

const press = css`
  transition: transform 160ms ${EASE_OUT}, box-shadow 160ms ${EASE_OUT}, background 160ms ${EASE_OUT};
  &:active:not(:disabled) {
    transform: translate(3px, 4px) scale(0.98);
    box-shadow: 1px 1px 0 ${C.ink};
  }
  &:focus-visible {
    outline: 3px dashed ${C.ink};
    outline-offset: 5px;
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
  ${hover(css`
    &:hover:not(:disabled) {
      transform: translate(-1px, -2px) rotate(-1.2deg);
      box-shadow: 6px 8px 0 ${C.ink};
    }
  `)}
`;

/** The BUY ZEUS button, as a button or a link. */
export const InkButton = styled.button<{ $big?: boolean; $tone?: 'yellow' | 'white' | 'ink' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 52px;
  padding: ${(p) => (p.$big ? '16px 40px' : '12px 28px')};
  font-family: 'GROBOLD', sans-serif;
  font-size: ${(p) => (p.$big ? '26px' : '20px')};
  line-height: 1.1;
  color: ${(p) => (p.$tone === 'ink' ? C.white : C.ink)};
  text-decoration: none;
  text-align: center;
  background: ${(p) => (p.$tone === 'white' ? C.white : p.$tone === 'ink' ? C.ink : C.yellow)};
  border: 3.5px solid ${C.ink};
  border-radius: 999px 860px 920px 980px / 980px 900px 960px 880px;
  box-shadow: 4px 5px 0 ${C.ink};
  cursor: pointer;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  ${press}
`;

export const TextButton = styled.button`
  padding: 6px 2px;
  border: 0;
  background: none;
  font: 700 15px 'Ubuntu', sans-serif;
  color: inherit;
  text-decoration: underline;
  text-decoration-thickness: 2px;
  text-underline-offset: 3px;
  cursor: pointer;
  min-height: 44px;
`;

export const FieldLabel = styled.label`
  display: block;
  margin: 0 0 8px;
  font-family: 'GROBOLD', sans-serif;
  font-size: 18px;
`;

/** A text field in ink, with an optional suffix drawn inside it. */
export const FieldBox = styled.div`
  display: flex;
  align-items: stretch;
  min-height: 58px;
  background: ${C.white};
  border: 3.5px solid ${C.ink};
  border-radius: 18px 22px 16px 24px / 20px 16px 22px 18px;
  box-shadow: inset 0 3px 0 rgba(11, 19, 43, 0.08);
  overflow: hidden;
  transition: box-shadow 160ms ${EASE_OUT};
  &:focus-within {
    box-shadow: 0 0 0 4px ${C.yellow};
  }
  input {
    flex: 1;
    min-width: 0;
    padding: 12px 16px;
    border: 0;
    outline: 0;
    background: transparent;
    font: 700 20px 'Ubuntu Mono', monospace;
    color: ${C.ink};
  }
  input::placeholder {
    color: rgba(11, 19, 43, 0.38);
  }
  .suffix {
    display: flex;
    align-items: center;
    padding: 0 16px;
    background: ${C.mist};
    border-left: 3px solid ${C.ink};
    font: 700 17px 'Ubuntu Mono', monospace;
    white-space: nowrap;
  }
  .side {
    display: flex;
    align-items: center;
    padding: 6px;
  }
`;

export type Tone = 'info' | 'good' | 'bad';

/** One line that says what is happening, read out to screen readers too. */
export const Note: React.FC<{ tone: Tone; children: React.ReactNode }> = ({ tone, children }) => (
  <NoteBox $tone={tone} role={tone === 'bad' ? 'alert' : 'status'}>
    {tone === 'info' && <Spinner aria-hidden="true" />}
    <span>{children}</span>
  </NoteBox>
);

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Spinner = styled.i`
  flex: none;
  width: 18px;
  height: 18px;
  border: 3px solid ${C.ink};
  border-right-color: transparent;
  border-radius: 50%;
  animation: ${spin} 800ms linear infinite;
  @media (prefers-reduced-motion: reduce) {
    animation-duration: 2.4s;
  }
`;

const NoteBox = styled.p<{ $tone: Tone }>`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 16px 0 0;
  padding: 12px 16px;
  font-weight: 700;
  font-size: 16px;
  line-height: 1.4;
  overflow-wrap: anywhere;
  border: 3px solid ${C.ink};
  border-radius: 16px 20px 14px 22px / 18px 14px 20px 16px;
  background: ${(p) => (p.$tone === 'good' ? '#C9F2CF' : p.$tone === 'bad' ? '#FFD3E8' : C.white)};
`;

/* ---------------------------------------------------------------- foot */

export const Foot: React.FC = () => (
  <FootBox>
    <div className="join">
      <img src="/redesign2/scenes/gang.webp" alt="Zeus celebrating with Pepe, Brett, Andy and Landwolf" loading="lazy" />
      <div>
        <Buy big />
        <Socials color={C.ink} size={30} />
      </div>
    </div>
    <DrawnFooter color={C.ink} />
  </FootBox>
);

const FootBox = styled.div`
  .join {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: clamp(20px, 4vw, 60px);
    flex-wrap: wrap;
    max-width: 1240px;
    margin: 0 auto;
    padding: 20px clamp(16px, 4vw, 40px) 40px;
  }
  .join img {
    width: min(460px, 80vw);
    height: auto;
    border: 4px solid ${C.ink};
    border-radius: 28px 36px 24px 40px / 34px 26px 38px 28px;
    box-shadow: 6px 8px 0 ${C.ink};
    transform: rotate(-2deg);
  }
  .join > div {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 22px;
  }
`;
